import { filter, map } from 'lodash';
import { useCallback, useMemo } from 'react';
import { useQuery } from 'react-query';

import { payoutManualApi } from 'api';
import { MANUAL_BATCH_MAX_ORDERS } from 'constants/order.constants';
import { QueryKey } from 'enums';
import { PayoutManualBatchItem, PayoutManualBatchItemResult } from 'types';
import { filtersUtils } from 'utils';

export const useRetryableManualOrders = (
  items: PayoutManualBatchItem[] | undefined,
  enabled: boolean,
) => {
  const filteredCandidateIds = useMemo(
    () =>
      map(
        filter(
          items,
          (item) =>
            item.result === PayoutManualBatchItemResult.Failed ||
            item.result === PayoutManualBatchItemResult.Skipped,
        ),
        'orderId',
      ),
    [items],
  );

  const queryResult = useQuery(
    [QueryKey.ManualPayoutOrders, 'retryable', filteredCandidateIds],
    () =>
      payoutManualApi.getQueuePaginated({
        filters: filtersUtils.encode(
          JSON.stringify({
            orderIdsConcatenated: filteredCandidateIds.join(','),
          }),
        ),
        page: 1,
        take: MANUAL_BATCH_MAX_ORDERS,
      }),
    {
      enabled: enabled && filteredCandidateIds.length > 0,
      retry: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  );

  const filteredRetryableIds = useMemo(
    () =>
      map(
        filter(queryResult.data?.items, (order) => !order.lock),
        'id',
      ),
    [queryResult.data?.items],
  );

  const hasNoRetryableOrders = useMemo(
    () =>
      filteredCandidateIds.length > 0 &&
      queryResult.isSuccess &&
      !queryResult.isFetching &&
      filteredRetryableIds.length === 0,
    [
      filteredCandidateIds.length,
      queryResult.isSuccess,
      queryResult.isFetching,
      filteredRetryableIds.length,
    ],
  );

  const { refetch } = queryResult;
  const getRetryableIds = useCallback(async () => {
    if (!filteredCandidateIds.length) {
      return [];
    }
    const result = await refetch();
    if (result.isError) {
      return [];
    }
    return map(
      filter(result.data?.items, (order) => !order.lock),
      'id',
    );
  }, [filteredCandidateIds.length, refetch]);

  return {
    filteredRetryableIds,
    hasNoRetryableOrders,
    isChecking: queryResult.isFetching,
    getRetryableIds,
  };
};
